import axios, { AxiosError } from "axios";
import { env } from "@/config";
import {
  AssistedAffiliationValidationItem,
  AssistedAffiliationValidationName,
  AssistedAffiliationValidationResponse,
  AssistedAffiliationValidations,
  RncValidationFeedback,
} from "@/screens/AssistedAffiliationRnc/types";
import {
  RNC_VALIDATION_SUCCESS_LINES,
} from "@/screens/AssistedAffiliationRnc/constants";

const validateIdentificationEndpoint =
  "/v1/assisted-affiliation/validate";

const validationNames: AssistedAffiliationValidationName[] = [
  "dgii_validation",
  "billet_validation",
  "cardnet_validation",
  "request_validation",
];

const failedValidationCopy: Record<
  AssistedAffiliationValidationName,
  {
    incident: string;
    kind: RncValidationFeedback["kind"];
    tone: RncValidationFeedback["tone"];
  }
> = {
  dgii_validation: {
    incident: "RNC no activo ante DGII",
    kind: "inactiveInDgii",
    tone: "error",
  },
  billet_validation: {
    incident: "Comercio existente en Billet",
    kind: "existsInBillet",
    tone: "warning",
  },
  cardnet_validation: {
    incident: "Comercio existente en Cardnet",
    kind: "existsInCardnet",
    tone: "warning",
  },
  request_validation: {
    incident: "Solicitud en curso",
    kind: "duplicatedRequest",
    tone: "warning",
  },
};

const buildValidateIdentificationPayload = (
  documentNumber: string,
  names: AssistedAffiliationValidationName[] = validationNames
) => ({
  validations: names.map((name) => ({
    name,
    value: documentNumber.trim(),
  })),
});

const isFailedValidation = (validation: AssistedAffiliationValidationItem) =>
  validation.code.endsWith("_002");

const getFailedValidations = (
  response: AssistedAffiliationValidationResponse
) => response.data?.validations.filter(isFailedValidation) || [];

const buildSingleFailureFeedback = (
  validation: AssistedAffiliationValidationItem
): RncValidationFeedback => {
  const copy = failedValidationCopy[validation.name];

  if (validation.name === "billet_validation") {
    return {
      kind: copy.kind,
      tone: copy.tone,
      description: validation.message,
      commercialName: validation.commercial_name,
    };
  }

  if (validation.name === "cardnet_validation") {
    return {
      kind: copy.kind,
      tone: copy.tone,
      description: validation.message,
      mid: validation.mid,
    };
  }

  if (validation.name === "dgii_validation") {
    return {
      kind: copy.kind,
      tone: copy.tone,
      description: validation.message,
      foundStatus: validation.found_status || "Suspendido",
    };
  }

  return {
    kind: copy.kind,
    tone: copy.tone,
    description: validation.message,
    registryNumber: validation.registry_number,
    status: validation.status,
  };
};

const buildMultipleIncidentsFeedback = (
  validations: AssistedAffiliationValidationItem[]
): RncValidationFeedback => ({
  kind: "multipleIncidents",
  tone: "error",
  title: "Se encontraron incidencias:",
  incidents: validations
    .map((validation) => failedValidationCopy[validation.name]?.incident)
    .filter(Boolean),
});

const mapValidationResponseToFeedback = (
  response: AssistedAffiliationValidationResponse
): RncValidationFeedback => {
  const failedValidations = getFailedValidations(response);

  if (failedValidations.length === 0) {
    return {
      kind: "success",
      tone: "success",
      lines: RNC_VALIDATION_SUCCESS_LINES,
    };
  }

  if (response.code === "MI-001" || failedValidations.length > 1) {
    return buildMultipleIncidentsFeedback(failedValidations);
  }

  const [failedValidation] = failedValidations;
  return buildSingleFailureFeedback(failedValidation);
};

const hasAtLeastOneDayElapsed = (lastValidationAt?: string) => {
  if (!lastValidationAt) return true;

  const lastValidationDate = new Date(lastValidationAt);
  if (Number.isNaN(lastValidationDate.getTime())) return true;

  const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
  return Date.now() - lastValidationDate.getTime() >= oneDayInMilliseconds;
};

const buildEditValidationNames = (
  lastValidationAt?: string
): AssistedAffiliationValidationName[] => {
  const names: AssistedAffiliationValidationName[] = ["billet_validation"];

  // C05-10047: Billet se valida siempre; DGII/Cardnet solo si pasó al menos un día.
  if (hasAtLeastOneDayElapsed(lastValidationAt)) {
    names.push("dgii_validation", "cardnet_validation");
  }

  return names;
};

const applyValidationResult = (
  currentValidations: AssistedAffiliationValidations,
  validation: AssistedAffiliationValidationItem
): AssistedAffiliationValidations => {
  const failed = isFailedValidation(validation);

  if (validation.name === "dgii_validation") {
    return { ...currentValidations, valid_DGI: !failed };
  }

  if (validation.name === "billet_validation") {
    return { ...currentValidations, exists_in_billet: failed };
  }

  if (validation.name === "cardnet_validation") {
    return { ...currentValidations, is_cardnet_client: failed };
  }

  return currentValidations;
};

const mapValidationResponseToValidations = (
  response: AssistedAffiliationValidationResponse,
  previousValidations: AssistedAffiliationValidations
): AssistedAffiliationValidations =>
  (response.data?.validations || []).reduce(
    applyValidationResult,
    previousValidations
  );

const postValidateIdentification = async (
  documentNumber: string,
  names?: AssistedAffiliationValidationName[]
) =>
  axios.post<AssistedAffiliationValidationResponse>(
    `${env.ASSISTED_AFFILIATION_API_URL}${validateIdentificationEndpoint}`,
    buildValidateIdentificationPayload(documentNumber, names),
    {
      headers: { "Content-Type": "application/json" },
    }
  );

export const validateIdentificationService = async (
  documentNumber: string
): Promise<RncValidationFeedback> => {
  try {
    const response = await postValidateIdentification(documentNumber);

    return mapValidationResponseToFeedback(response.data);
  } catch (error) {
    const axiosError =
      error as AxiosError<AssistedAffiliationValidationResponse>;

    if (axiosError.response?.data?.data?.validations) {
      return mapValidationResponseToFeedback(axiosError.response.data);
    }

    throw error;
  }
};

export const validateIdentificationChangesService = async ({
  documentNumber,
  lastValidationAt,
  previousValidations,
}: {
  documentNumber: string;
  lastValidationAt?: string;
  previousValidations: AssistedAffiliationValidations;
}): Promise<AssistedAffiliationValidations> => {
  const names = buildEditValidationNames(lastValidationAt);

  try {
    const response = await postValidateIdentification(documentNumber, names);

    return mapValidationResponseToValidations(
      response.data,
      previousValidations
    );
  } catch (error) {
    const axiosError =
      error as AxiosError<AssistedAffiliationValidationResponse>;

    if (axiosError.response?.data?.data?.validations) {
      return mapValidationResponseToValidations(
        axiosError.response.data,
        previousValidations
      );
    }

    throw error;
  }
};

export const getValidateIdentificationErrorMessage = (error: unknown) => {
  const axiosError =
    error as AxiosError<AssistedAffiliationValidationResponse | string>;
  const responseData = axiosError.response?.data;

  if (typeof responseData === "string") {
    return responseData;
  }

  return (
    responseData?.message ||
    axiosError.message ||
    "No se pudo validar la identificación del comercio."
  );
};
